import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  normalizeSharedDateWindowPeriod,
  readSharedDateWindowFromSession,
  STORE_INSIGHTS_SHARED_DATE_WINDOW_SESSION_KEY,
  type StoreInsightsSharedDateWindowPeriod,
  writeSharedDateWindowToSession,
} from "@/pages/store-insights/shared/storeInsightsSelectionSession";
import { type DateRange, storePerformanceService } from "@/services/StorePerformanceService";
import {
  getBrowserTimezone,
  getEndOfStoreDay,
  getStartOfStoreDay,
  parseStoreDateKey,
} from "@/utils/storeTime";

interface UseStoreInsightsSharedDateWindowOptions {
  defaultPeriod?: StoreInsightsSharedDateWindowPeriod;
  availablePeriods?: readonly StoreInsightsSharedDateWindowPeriod[];
  sessionKey?: string;
}

const DEFAULT_PERIOD: StoreInsightsSharedDateWindowPeriod = "yesterday";

const areRangesEqual = (left: DateRange | undefined, right: DateRange | undefined) => {
  if (!left && !right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return (
    left.start.getTime() === right.start.getTime() && left.end.getTime() === right.end.getTime()
  );
};

export const useStoreInsightsSharedDateWindow = ({
  defaultPeriod = DEFAULT_PERIOD,
  availablePeriods,
  sessionKey = STORE_INSIGHTS_SHARED_DATE_WINDOW_SESSION_KEY,
}: UseStoreInsightsSharedDateWindowOptions = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const browserTimezone = getBrowserTimezone();

  const [period, setPeriod] = useState<StoreInsightsSharedDateWindowPeriod>(defaultPeriod);
  const [customRange, setCustomRange] = useState<DateRange | undefined>(undefined);
  const [customPending, setCustomPending] = useState(false);

  const lastNonCustomRangeRef = useRef<DateRange | undefined>(undefined);
  const lastInternalSearchRef = useRef<string | null>(null);
  const currentSearch = searchParams.toString();
  const hasPendingInternalSearchUpdate = lastInternalSearchRef.current !== null;
  const availablePeriodsKey = availablePeriods?.join(",");
  const availablePeriodSet = useMemo(
    () => (availablePeriods ? new Set(availablePeriods) : null),
    // The joined key keeps equivalent inline arrays from invalidating hook callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availablePeriodsKey],
  );
  const effectiveDefaultPeriod = useMemo(() => {
    if (!availablePeriodSet || availablePeriodSet.has(defaultPeriod)) {
      return defaultPeriod;
    }

    return availablePeriodSet.values().next().value ?? DEFAULT_PERIOD;
  }, [availablePeriodSet, defaultPeriod]);
  const ensureAvailablePeriod = useCallback(
    (nextPeriod: StoreInsightsSharedDateWindowPeriod) =>
      !availablePeriodSet || availablePeriodSet.has(nextPeriod)
        ? nextPeriod
        : effectiveDefaultPeriod,
    [availablePeriodSet, effectiveDefaultPeriod],
  );

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      const params = new URLSearchParams(searchParams);
      let changed = false;

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          if (params.has(key)) {
            params.delete(key);
            changed = true;
          }
        } else if (params.get(key) !== value) {
          params.set(key, value);
          changed = true;
        }
      });

      if (!changed) {
        return;
      }

      lastInternalSearchRef.current = params.toString();
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const fmtYMD = useCallback(
    (date: Date) => storePerformanceService.formatDate(date, browserTimezone),
    [browserTimezone],
  );

  const getRangeForPreset = useCallback(
    (nextPeriod: StoreInsightsSharedDateWindowPeriod): DateRange => {
      const now = new Date();

      switch (nextPeriod) {
        case "custom":
          return customRange ?? storePerformanceService.getDailyRange(now, browserTimezone);
        case "week_to_date":
          return storePerformanceService.getWeekRange(now, browserTimezone);
        case "last_4_weeks":
          return storePerformanceService.getLast4WeeksRange(now, browserTimezone);
        case "last_weekend":
          return storePerformanceService.getLastWeekendRange(now, browserTimezone);
        case "yesterday":
          return storePerformanceService.getYesterdayRange(now, browserTimezone);
        case "today":
        default:
          return storePerformanceService.getDailyRange(now, browserTimezone);
      }
    },
    [browserTimezone, customRange],
  );

  const currentDateRange = useMemo(() => {
    if (period === "custom") {
      return customRange ?? lastNonCustomRangeRef.current ?? getRangeForPreset("today");
    }

    return getRangeForPreset(period);
  }, [customRange, getRangeForPreset, period]);

  useEffect(() => {
    if (period !== "custom") {
      lastNonCustomRangeRef.current = getRangeForPreset(period);
    }
  }, [getRangeForPreset, period]);

  useEffect(() => {
    // Avoid reconciling from stale URL params while an internal `setSearchParams`
    // update is still in flight; otherwise the hook can briefly snap back to the
    // old range before the router commits the new query string.
    if (customPending || hasPendingInternalSearchUpdate) {
      return;
    }

    const periodParam = searchParams.get("period");
    const startParam = searchParams.get("start");
    const endParam = searchParams.get("end");
    const sessionState = readSharedDateWindowFromSession(sessionKey);

    let nextPeriod = period;
    let nextCustomRange = customRange;
    const normalizedPeriodParam = normalizeSharedDateWindowPeriod(periodParam);

    const startDate = parseStoreDateKey(startParam, browserTimezone);
    const endDate = parseStoreDateKey(endParam, browserTimezone);

    if (normalizedPeriodParam === "custom" && startDate && endDate) {
      nextPeriod = ensureAvailablePeriod("custom");
      nextCustomRange = {
        start: getStartOfStoreDay(startDate, browserTimezone),
        end: getEndOfStoreDay(endDate, browserTimezone),
      };
    } else if (normalizedPeriodParam && normalizedPeriodParam !== "custom") {
      nextPeriod = ensureAvailablePeriod(normalizedPeriodParam);
      nextCustomRange = undefined;
    } else if (startDate && endDate) {
      nextPeriod = ensureAvailablePeriod("custom");
      nextCustomRange = {
        start: getStartOfStoreDay(startDate, browserTimezone),
        end: getEndOfStoreDay(endDate, browserTimezone),
      };
    } else if (!hasPendingInternalSearchUpdate && sessionState) {
      nextPeriod = ensureAvailablePeriod(sessionState.period);

      if (nextPeriod === "custom" && sessionState.start && sessionState.end) {
        const sessionStart = parseStoreDateKey(sessionState.start, browserTimezone);
        const sessionEnd = parseStoreDateKey(sessionState.end, browserTimezone);

        if (sessionStart && sessionEnd) {
          nextCustomRange = {
            start: getStartOfStoreDay(sessionStart, browserTimezone),
            end: getEndOfStoreDay(sessionEnd, browserTimezone),
          };
        } else {
          nextPeriod = effectiveDefaultPeriod;
          nextCustomRange = undefined;
        }
      } else {
        nextCustomRange = undefined;
      }
    } else if (!hasPendingInternalSearchUpdate) {
      nextPeriod = effectiveDefaultPeriod;
      nextCustomRange = undefined;
    }

    if (period !== nextPeriod) {
      setPeriod(nextPeriod);
    }

    if (!areRangesEqual(customRange, nextCustomRange)) {
      setCustomRange(nextCustomRange);
    }

    const desiredRange =
      nextPeriod === "custom"
        ? (nextCustomRange ?? getRangeForPreset("today"))
        : getRangeForPreset(nextPeriod);
    const desiredStart = fmtYMD(desiredRange.start);
    const desiredEnd = fmtYMD(desiredRange.end);

    const desiredPeriodParam = nextPeriod;
    const desiredStartParam = nextPeriod === "custom" ? desiredStart : null;
    const desiredEndParam = nextPeriod === "custom" ? desiredEnd : null;

    if (
      periodParam !== desiredPeriodParam ||
      startParam !== desiredStartParam ||
      endParam !== desiredEndParam
    ) {
      updateSearchParams({
        period: desiredPeriodParam,
        start: desiredStartParam,
        end: desiredEndParam,
      });
    }

    writeSharedDateWindowToSession(sessionKey, {
      period: nextPeriod,
      start: desiredStart,
      end: desiredEnd,
    });
  }, [
    browserTimezone,
    customPending,
    customRange,
    effectiveDefaultPeriod,
    ensureAvailablePeriod,
    fmtYMD,
    getRangeForPreset,
    hasPendingInternalSearchUpdate,
    period,
    searchParams,
    sessionKey,
    updateSearchParams,
  ]);

  useEffect(() => {
    if (lastInternalSearchRef.current === currentSearch) {
      lastInternalSearchRef.current = null;
    }
  }, [currentSearch]);

  const onPeriodChange = useCallback(
    (nextPeriod: StoreInsightsSharedDateWindowPeriod) => {
      const availablePeriod = ensureAvailablePeriod(nextPeriod);
      setPeriod(availablePeriod);
      const isCustom = availablePeriod === "custom";
      setCustomPending(isCustom);

      if (!isCustom) {
        const nextRange = getRangeForPreset(availablePeriod);
        const start = fmtYMD(nextRange.start);
        const end = fmtYMD(nextRange.end);

        updateSearchParams({
          period: availablePeriod,
          start: null,
          end: null,
        });
        writeSharedDateWindowToSession(sessionKey, {
          period: availablePeriod,
          start,
          end,
        });
      }
    },
    [ensureAvailablePeriod, fmtYMD, getRangeForPreset, sessionKey, updateSearchParams],
  );

  const onCustomRangeChange = useCallback(
    (nextRange: DateRange) => {
      setCustomRange(nextRange);
      setCustomPending(false);

      const start = fmtYMD(nextRange.start);
      const end = fmtYMD(nextRange.end);

      updateSearchParams({
        period: "custom",
        start,
        end,
      });
      writeSharedDateWindowToSession(sessionKey, {
        period: "custom",
        start,
        end,
      });
    },
    [fmtYMD, sessionKey, updateSearchParams],
  );

  return {
    period,
    customRange,
    currentDateRange,
    onPeriodChange,
    onCustomRangeChange,
  };
};
